import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  DropdownItemProps,
  Loader,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";
import { CoachService } from "../../../../../services";
import { map } from "lodash";
import { useSocialCoachSession } from "../../../../../utils";
import { Branch, Region } from "../../../../../interfaces";
import "./styles.scss";
import { ContentPageTheme, ContentPageThemeDefault, ContentPageThemeServices } from "@/services/bedrock";

interface VPTEProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onSaved: (defaults: ContentPageThemeDefault[]) => void;
  themes: ContentPageTheme[];
  defaults: ContentPageThemeDefault[];
}

type Props = VPTEProps;

export const VideoPageThemeDefaultsEditor: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  themes,
  defaults,
  loading,
}) => {
  const { coachId } = useSocialCoachSession();
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [themeDefaults, setThemeDefaults] = useState<ContentPageThemeDefault[]>(defaults);

  const themeOptions: DropdownItemProps[] = useMemo(() => {
    return themes.map(theme => {
      return {
        key: theme.id,
        text: theme.name,
        value: theme.id,
      } as DropdownItemProps;
    });
  }, [themes]);

  useEffect(() => {
    if (coachId) {
      setLoadingBranches(true);
      CoachService.branches(coachId).then(response => {
        setBranches(response);
        setLoadingBranches(false);
      });

      setLoadingRegions(true);
      CoachService.regions(coachId).then(response => {
        setRegions(response);
        setLoadingRegions(false);
      });
    }
  }, [coachId]);

  useEffect(() => {
    setThemeDefaults(defaults);
  }, [defaults]);

  const updateDefaults = () => {
    ContentPageThemeServices.updatePageThemeDefaults(themeDefaults).then(onSaved);
  };

  const themeDropdown = (segmentationDetailId: string | undefined) => {
    const defaultValue = themeDefaults.find(d => d.segmentationDetailId == segmentationDetailId);

    return (
      <TableCell className={"dropdownCell"}>
        <Dropdown
          loading={loading}
          options={themeOptions}
          defaultValue={defaultValue?.themeId}
          onChange={(_, d) => {
            setThemeDefaults(list => {
              const others = list.filter(d => d.segmentationDetailId != segmentationDetailId);
              if (!!d.value) {
                const newDefault = {
                  coachId,
                  segmentationDetailId,
                  themeId: d.value as number,
                };
                return [newDefault, ...others];
              } else {
                return [...others];
              }
            });
          }}
          clearable
          selection
          fluid
        />
      </TableCell>
    );
  };

  return (
    <Modal open={open} onClose={onClose} className={"videoPageThemeDefaultsEditor"}>
      <Modal.Header>Theme Defaults Editor</Modal.Header>
      <Modal.Content>
        <div className={"tableContainer"}>
          <Table compact attached fixed>
            <TableHeader>
              <TableRow>
                <TableHeaderCell colSpan="2">Account Default</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Account Default</TableCell>
                {themeDropdown(undefined)}
              </TableRow>
            </TableBody>
          </Table>

          {(loadingRegions || regions.length > 0) && (
            <Table compact attached fixed>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell colSpan="2">Regions</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingRegions && (
                  <TableRow>
                    <TableHeaderCell colSpan="2">
                      <Loader active />
                    </TableHeaderCell>
                  </TableRow>
                )}
                {!loadingRegions &&
                  regions.map(region => {
                    return (
                      <TableRow key={`region-defaults-${region.regionId}`}>
                        <TableCell>{region.name}</TableCell>
                        {themeDropdown(region.regionId)}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}

          {(loadingBranches || branches.length > 0) && (
            <Table compact attached fixed>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell colSpan="2">Branches</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingBranches && (
                  <TableRow>
                    <TableHeaderCell colSpan="2">
                      <Loader active />
                    </TableHeaderCell>
                  </TableRow>
                )}
                {!loadingBranches &&
                  map(branches, branch => {
                    return (
                      <TableRow key={`branch-defaults-${branch.branchId}`}>
                        <TableCell>{branch.name}</TableCell>
                        {themeDropdown(branch.branchId)}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} secondary>
          Close
        </Button>

        <Button onClick={updateDefaults} primary>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
